import React from 'react'

import type { OffsetOptions, OpenChangeReason, Placement, ReferenceType, ShiftOptions } from '@floating-ui/react'
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingPortal,
  FloatingTree,
  offset,
  shift,
  useClick,
  useClientPoint,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import type { HTMLAttributes } from 'react'
import styled, { css } from 'styled-components'

import { useFloatingTransitionStyles } from '@libs/typescript-utils'

import { FLOATING_TRANSITION_DURATION } from '@constants/theme'

export const PopoverContainer = styled.div<{ $decreaseZIndex?: boolean }>`
${({ $decreaseZIndex, theme }) => css`
  z-index: ${$decreaseZIndex ? theme.zIndex.dialog - 1 : theme.zIndex.dialog};
`}
}`

export const PopoverPaper = styled.div`
${({ theme }) => css`
  box-shadow: ${theme.shadows[100]};
  border-radius: 4px;
  border: 1px solid ${theme.colors.common.paperStroke};
  background-color: ${theme.colors.gray[0]};
  max-width: var(--max-width);
  max-height: var(--max-height);
`}
}`

export interface TriggerProps {
  ref: ((node: ReferenceType | null) => void) & ((node: ReferenceType | null) => void)
  onClick: (event: React.MouseEvent) => void
  type: 'button'
}

export interface OnOpenChangeCallbackProps {
  nextOpen: boolean
  event: Event | undefined
  reason: OpenChangeReason | undefined
  /** Transition duration in milliseconds until the popover is fully visible or hidden */
  transitionDuration: number
}

interface CommonPopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'style'> {
  anchorPosition?: {
    x: number
    y: number
  }
  children: React.ReactNode
  decreaseZIndex?: boolean
  maxWidth?: string
  maxHeight?: string
  modalMode?: boolean
  offset?: OffsetOptions
  onOpenChangeCallback?: (params: OnOpenChangeCallbackProps) => void
  placement?: Placement
  shift?: ShiftOptions
  /** Needs to be a ReactElement that accepts a ref and an onClick function */
  trigger?: React.ReactElement<TriggerProps>
  withoutPortal?: boolean
  withoutScrollLock?: boolean
}

export interface ControlledPopoverProps extends CommonPopoverProps {
  open: boolean
  /** Popover's own onOpenChange extended with transitionDuration being sent back */
  onOpenChange: (open: boolean, event?: Event, reason?: OpenChangeReason, transitionDuration?: number) => void
}

export interface UncontrolledPopoverProps extends CommonPopoverProps {
  open?: undefined
  onOpenChange?: undefined
}

export type PopoverProps = ControlledPopoverProps | UncontrolledPopoverProps

function PopoverComponent({
  anchorPosition,
  children,
  className,
  decreaseZIndex = false,
  maxHeight,
  maxWidth,
  modalMode = false,
  offset: offsetOption = 0,
  onOpenChange: setControlledOpen,
  onOpenChangeCallback,
  open: controlledOpen,
  placement = 'bottom',
  shift: shiftOption,
  trigger,
  withoutPortal = false,
  withoutScrollLock = false,
  ...rest
}: PopoverProps) {
  const [isUncontrolledOpen, setIsUncontrolledOpen] = React.useState(false)

  const open = controlledOpen ?? isUncontrolledOpen
  const setOpen = setControlledOpen ?? setIsUncontrolledOpen
  // Subscribe this component to the <FloatingTree> wrapper:
  const nodeId = useFloatingNodeId()

  const { refs, context, floatingStyles } = useFloating({
    placement,
    middleware: [offset(offsetOption), shift(shiftOption)],
    nodeId,
    open,
    onOpenChange: (nextOpen, event, reason) => {
      setOpen(nextOpen, event, reason, FLOATING_TRANSITION_DURATION)
      onOpenChangeCallback?.({
        nextOpen,
        event,
        reason,
        transitionDuration: FLOATING_TRANSITION_DURATION,
      })
    },
    whileElementsMounted: autoUpdate,
  })
  const { isMounted, styles } = useFloatingTransitionStyles(context, placement, FLOATING_TRANSITION_DURATION)

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context)
  // For anchor positioning, we need to use the client point.
  const clientPoint = useClientPoint(context, {
    enabled: anchorPosition !== undefined,
    x: anchorPosition?.x,
    y: anchorPosition?.y,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([clientPoint, click, dismiss, role])

  // Pass the extra props to the trigger that is handling the floating ui logic.
  let triggerWithExtraProps = trigger
  // Checking isValidElement is the safe way and avoids a typescript error too.
  if (React.isValidElement(trigger)) {
    triggerWithExtraProps = React.cloneElement(trigger as React.ReactElement<TriggerProps>, {
      ref: refs.setReference,
      type: 'button',
      ...getReferenceProps(),
    })
  }

  const PortalWrapper = withoutPortal ? React.Fragment : FloatingPortal

  return (
    <>
      {triggerWithExtraProps}
      <FloatingNode id={nodeId}>
        {isMounted && (
          <PortalWrapper>
            {!withoutScrollLock && <FloatingOverlay lockScroll />}
            <FloatingFocusManager context={context} modal={modalMode}>
              <PopoverContainer ref={refs.setFloating} style={floatingStyles} $decreaseZIndex={decreaseZIndex}>
                <PopoverPaper
                  className={className}
                  style={{
                    ...styles,
                    '--max-width': maxWidth,
                    '--max-height': maxHeight,
                  }}
                  {...rest}
                  {...getFloatingProps()}
                >
                  {children}
                </PopoverPaper>
              </PopoverContainer>
            </FloatingFocusManager>
          </PortalWrapper>
        )}
      </FloatingNode>
    </>
  )
}

// Popover component that wraps the FloatingTree to manage the floating UI context if this is a root component.
export function Popover(props: PopoverProps) {
  const parentId = useFloatingParentNodeId()

  // This is a root, so we wrap it with the tree
  if (parentId === null) {
    return (
      <FloatingTree>
        <PopoverComponent {...props} />
      </FloatingTree>
    )
  }

  return <PopoverComponent {...props} />
}
